import React, { useState, useEffect } from 'react';

const KartleggingLauncher = () => {
  const [currentStudentName, setCurrentStudentName] = useState('');
  const [studentList, setStudentList] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [moduleProgress, setModuleProgress] = useState({});
  const [showDataWarning, setShowDataWarning] = useState(true);

  // Hent elev-liste ved oppstart
  useEffect(() => {
    loadStudentList();
    
    // Sjekk om datavarsel er vist før
    const warningShown = sessionStorage.getItem('warning_shown');
    setShowDataWarning(!warningShown);
  }, []);

  // Last fremdrift når elev velges
  useEffect(() => {
    if (selectedStudent) {
      loadStudentProgress(selectedStudent);
    } else {
      setModuleProgress({});
    }
  }, [selectedStudent]);

  // Oppdater fremdrift når brukeren kommer tilbake fra andre sider
  useEffect(() => {
    const handleWindowFocus = () => {
      if (selectedStudent) {
        loadStudentProgress(selectedStudent);
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    
    // Cleanup
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [selectedStudent]);

  const loadStudentList = () => {
    try {
      const saved = sessionStorage.getItem('kartlegging_students');
      if (saved) {
        const students = JSON.parse(saved);
        setStudentList(students);
        
        // Hvis det bare er én elev, velg den automatisk
        if (students.length === 1) {
          setSelectedStudent(students[0].name);
        }
      }
    } catch (error) {
      console.error('Kunne ikke laste elev-liste:', error);
    }
  };

  const loadStudentProgress = (studentName) => {
    const modules = [
      { id: 'lesing', totalTasks: 17 }, // Fra lesekartleggeren
      { id: 'skriving', totalTasks: 45 }, // Fra skrivekartleggeren (10+20+15)
      { id: 'ordforraad', totalTasks: 0 }, // Oppdater dette med riktig antall når du vet det
      { id: 'muntlig', totalTasks: 0 },
      { id: 'uttale', totalTasks: 0 }
    ];
    
    const progress = {};

    modules.forEach(module => {
      try {
        const data = sessionStorage.getItem(`kartlegging_${module.id}_${studentName}`);
        if (data) {
          const moduleData = JSON.parse(data);
          
          if (moduleData.results) {
            // Beregn fremdrift basert på faktiske resultater
            const completed = Object.keys(moduleData.results).length;
            const total = module.totalTasks;
            
            progress[module.id] = {
              completed,
              total,
              percentage: total > 0 ? Math.round((completed / total) * 100) : 0
            };
          }
        } else {
          // Ingen data funnet for denne modulen
          progress[module.id] = {
            completed: 0,
            total: module.totalTasks,
            percentage: 0
          };
        }
      } catch (error) {
        console.error(`Kunne ikke laste fremdrift for ${studentName}/${module.id}:`, error);
        progress[module.id] = {
          completed: 0,
          total: module.totalTasks,
          percentage: 0
        };
      }
    });

    setModuleProgress(progress);
  };

  const addNewStudent = () => {
    if (!currentStudentName.trim()) {
      alert('Vennligst skriv inn elevens navn');
      return;
    }

    const studentName = currentStudentName.trim();
    
    // Sjekk om eleven allerede eksisterer
    if (studentList.find(s => s.name.toLowerCase() === studentName.toLowerCase())) {
      alert('Denne eleven er allerede registrert');
      return;
    }

    const newStudent = {
      name: studentName,
      created: new Date().toISOString()
    };

    const updatedList = [...studentList, newStudent];
    
    try {
      sessionStorage.setItem('kartlegging_students', JSON.stringify(updatedList));
      setStudentList(updatedList);
      setSelectedStudent(studentName);
      setCurrentStudentName('');
      
      // Sett som aktiv elev for kartleggingsmodulene
      const studentData = {
        name: studentName,
        startTime: new Date().toISOString()
      };
      sessionStorage.setItem('kartlegging_student', JSON.stringify(studentData));
      
    } catch (error) {
      console.error('Kunne ikke lagre elev:', error);
      alert('Feil ved lagring av elev');
    }
  };

  const selectStudent = (studentName) => {
    setSelectedStudent(studentName);
    
    // Sett som aktiv elev for kartleggingsmodulene
    const studentData = {
      name: studentName,
      startTime: new Date().toISOString()
    };
    
    try {
      sessionStorage.setItem('kartlegging_student', JSON.stringify(studentData));
    } catch (error) {
      console.error('Kunne ikke sette aktiv elev:', error);
    }
  };

  const deleteStudent = (studentName) => {
    if (!window.confirm(`Er du sikker på at du vil slette all kartleggingsdata for ${studentName}?\n\nDette kan ikke angres.`)) {
      return;
    }

    // Slett moduldata for eleven
    const modules = ['lesing', 'skriving', 'ordforraad', 'muntlig', 'uttale'];
    modules.forEach(module => {
      sessionStorage.removeItem(`kartlegging_${module}_${studentName}`);
    });

    // Fjern fra elev-liste
    const updatedList = studentList.filter(s => s.name !== studentName);
    sessionStorage.setItem('kartlegging_students', JSON.stringify(updatedList));
    setStudentList(updatedList);

    // Hvis dette var den valgte eleven, clear selection
    if (selectedStudent === studentName) {
      setSelectedStudent('');
      setModuleProgress({});
      sessionStorage.removeItem('kartlegging_student');
    }

    alert(`Data for ${studentName} er slettet`);
  };

  const startModule = (moduleName) => {
    if (!selectedStudent) {
      alert('Vennligst velg en elev først');
      return;
    }

    // Sett som aktiv elev før navigering
    const studentData = {
      name: selectedStudent,
      startTime: new Date().toISOString()
    };
    
    try {
      sessionStorage.setItem('kartlegging_student', JSON.stringify(studentData));
      
      // Naviger til modul-apper
      const moduleUrls = {
        'lesing': './lesing/index.html',
        'skriving': './skriving/index.html',
        'ordforraad': './ord/index.html',
        'muntlig': './muntlig/index.html',
        'uttale': './uttale/index.html'
      };
      
      if (moduleUrls[moduleName]) {
        // Sjekk om modulen er tilgjengelig
        if (moduleName === 'lesing' || moduleName === 'skriving' || moduleName === 'ordforraad') {
          window.location.href = moduleUrls[moduleName];
        } else {
          alert(`${moduleName} er ikke implementert ennå.\nKommer i neste utviklingsfase.`);
        }
      } else {
        alert(`Ukjent modul: ${moduleName}`);
      }
    } catch (error) {
      console.error('Kunne ikke sette aktiv elev:', error);
      alert('Feil ved lagring av data. Prøv igjen.');
    }
  };

  const exportStudentProgress = (studentName) => {
    const modules = ['lesing', 'skriving', 'ordforraad', 'muntlig', 'uttale'];
    const exportData = {
      studentName,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    modules.forEach(module => {
      try {
        const data = sessionStorage.getItem(`kartlegging_${module}_${studentName}`);
        if (data) {
          exportData[module] = JSON.parse(data);
        }
      } catch (error) {
        console.error(`Feil ved eksport av ${studentName}/${module}:`, error);
      }
    });

    // Last ned som JSON-fil
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `kartlegging_${studentName.replace(/\s+/g, '_')}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAllProgress = () => {
    if (studentList.length === 0) {
      alert('Ingen elever å eksportere');
      return;
    }

    const allStudentsData = {
      exportDate: new Date().toISOString(),
      version: '1.0',
      students: {}
    };

    studentList.forEach(student => {
      const modules = ['lesing', 'skriving', 'ordforraad', 'muntlig', 'uttale'];
      const studentData = { name: student.name, created: student.created };

      modules.forEach(module => {
        try {
          const data = sessionStorage.getItem(`kartlegging_${module}_${student.name}`);
          if (data) {
            studentData[module] = JSON.parse(data);
          }
        } catch (error) {
          console.error(`Feil ved eksport av ${student.name}/${module}:`, error);
        }
      });

      allStudentsData.students[student.name] = studentData;
    });

    // Last ned som JSON-fil
    const dataStr = JSON.stringify(allStudentsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `kartlegging_alle_elever_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importProgress = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // Valider data-format
        if (data.version && data.exportDate) {
          
          // Håndter enkelt-elev eksport
          if (data.studentName) {
            // Sjekk om eleven allerede eksisterer
            const existingStudent = studentList.find(s => s.name.toLowerCase() === data.studentName.toLowerCase());
            
            if (!existingStudent) {
              // Legg til eleven i listen
              const newStudent = {
                name: data.studentName,
                created: new Date().toISOString()
              };
              const updatedList = [...studentList, newStudent];
              setStudentList(updatedList);
              sessionStorage.setItem('kartlegging_students', JSON.stringify(updatedList));
            }
            
            // Sett som valgt elev
            setSelectedStudent(data.studentName);

            // Gjenopprett moduldata for denne eleven
            const modules = ['lesing', 'skriving', 'ordforraad', 'muntlig', 'uttale'];
            modules.forEach(module => {
              if (data[module]) {
                sessionStorage.setItem(`kartlegging_${module}_${data.studentName}`, JSON.stringify(data[module]));
              }
            });

            // Oppdater fremdrift for valgt elev
            loadStudentProgress(data.studentName);
            
            alert(`Kartleggingsdata for ${data.studentName} er lastet inn!`);
          }
          
          // Håndter alle-elever eksport
          else if (data.students) {
            const importedStudents = [];
            
            Object.keys(data.students).forEach(studentName => {
              const studentData = data.students[studentName];
              
              // Legg til/oppdater eleven i listen
              const existingIndex = studentList.findIndex(s => s.name.toLowerCase() === studentName.toLowerCase());
              const student = {
                name: studentName,
                created: studentData.created || new Date().toISOString()
              };
              
              if (existingIndex === -1) {
                importedStudents.push(student);
              }
              
              // Gjenopprett moduldata
              const modules = ['lesing', 'skriving', 'ordforraad', 'muntlig', 'uttale'];
              modules.forEach(module => {
                if (studentData[module]) {
                  sessionStorage.setItem(`kartlegging_${module}_${studentName}`, JSON.stringify(studentData[module]));
                }
              });
            });
            
            // Oppdater elev-liste
            const updatedList = [...studentList, ...importedStudents];
            setStudentList(updatedList);
            sessionStorage.setItem('kartlegging_students', JSON.stringify(updatedList));
            
            // Hvis bare en elev ble importert, velg den
            if (Object.keys(data.students).length === 1) {
              const studentName = Object.keys(data.students)[0];
              setSelectedStudent(studentName);
              loadStudentProgress(studentName);
            }
            
            alert(`Kartleggingsdata for ${Object.keys(data.students).length} elev(er) er lastet inn!`);
          }
          
        } else {
          alert('Ugyldig datafil - mangler versjon eller eksportdato');
        }
      } catch (error) {
        console.error('Feil ved import:', error);
        alert('Ugyldig datafil - kunne ikke lese JSON');
      }
    };
    reader.readAsText(file);
  };

  const clearAllData = () => {
    if (window.confirm('Er du sikker på at du vil slette all kartleggingsdata?\n\nDette kan ikke angres.')) {
      // Slett all moduldata for alle elever
      studentList.forEach(student => {
        const modules = ['lesing', 'skriving', 'ordforraad', 'muntlig', 'uttale'];
        modules.forEach(module => {
          sessionStorage.removeItem(`kartlegging_${module}_${student.name}`);
        });
      });
      
      // Slett elev-liste og andre data
      sessionStorage.removeItem('kartlegging_students');
      sessionStorage.removeItem('kartlegging_student');
      sessionStorage.removeItem('warning_shown');
      
      // Tilbakestill state
      setCurrentStudentName('');
      setStudentList([]);
      setSelectedStudent('');
      setModuleProgress({});
      setShowDataWarning(true);
      
      alert('All data er slettet');
    }
  };

  const dismissWarning = () => {
    setShowDataWarning(false);
    sessionStorage.setItem('warning_shown', 'true');
  };

  const modules = [
    {
      id: 'lesing',
      title: '📚 Lesing',
      description: 'Grunnleggende leseferdigheter og tekstforståelse',
      available: true
    },
    {
      id: 'skriving',
      title: '✍️ Skriving',
      description: 'Grunnleggende skriveferdigheter',
      available: true
    },
    {
      id: 'ordforraad',
      title: '📖 Ordforråd',
      description: 'Ordkunnskap og ordforståelse',
      available: true // Endret fra false til true
    },
    {
      id: 'muntlig',
      title: '🗣️ Muntlig kommunikasjon',
      description: 'Muntlige ferdigheter og kommunikasjon',
      available: false
    },
    {
      id: 'uttale',
      title: '🔊 Uttale',
      description: 'Uttale og fonologi',
      available: false
    }
  ];

  const ModuleCard = ({ module }) => {
    const progress = moduleProgress[module.id] || { completed: 0, total: 0, percentage: 0 };
    const hasProgress = progress.completed > 0;

    return (
      <div
        style={{
          padding: '24px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: hasProgress ? '2px solid #10b981' : module.available ? '2px solid #e5e7eb' : '2px solid #d1d5db',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          opacity: module.available ? 1 : 0.6,
          cursor: module.available && selectedStudent ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        onClick={() => module.available && startModule(module.id)}
      >
        {!module.available && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            backgroundColor: '#f59e0b',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            Kommer snart
          </div>
        )}

        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 8px 0'
        }}>
          {module.title}
        </h3>

        <p style={{
          color: '#6b7280',
          margin: '0 0 16px 0',
          fontSize: '14px',
          lineHeight: '1.4'
        }}>
          {module.description}
        </p>

        {/* Fremdrift */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px'
          }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Fremdrift</span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: hasProgress ? '#059669' : '#6b7280' }}>
              {progress.percentage}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress.percentage}%`,
              height: '100%',
              backgroundColor: hasProgress ? '#10b981' : '#d1d5db',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
          {progress.total > 0 && (
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
              {progress.completed}/{progress.total} oppgaver
            </div>
          )}
        </div>

        {/* Status */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px'
        }}>
          {!module.available ? (
            <span style={{ color: '#6b7280' }}>⏳ Under utvikling</span>
          ) : hasProgress ? (
            <span style={{ color: '#059669', fontWeight: '500' }}>✅ Påbegynt</span>
          ) : selectedStudent ? (
            <span style={{ color: '#3b82f6', fontWeight: '500' }}>▶️ Klar til start</span>
          ) : (
            <span style={{ color: '#6b7280' }}>⚠️ Velg elev først</span>
          )}
        </div>
      </div>
    );
  };

  const totalProgress = () => {
    if (!selectedStudent || Object.keys(moduleProgress).length === 0) return 0;

    const availableModules = modules.filter(m => m.available);
    if (availableModules.length === 0) return 0;

    const totalPercentage = availableModules.reduce((sum, module) => {
      return sum + (moduleProgress[module.id]?.percentage || 0);
    }, 0);

    return Math.round(totalPercentage / availableModules.length);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '40px 20px',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '48px' }}>📚</div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#111827',
            margin: 0
          }}>
            Grunnleggende norsk - Kartlegging
          </h1>
        </div>

        <p style={{
          fontSize: '18px',
          color: '#6b7280',
          margin: '0 0 32px 0',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Kartlegg elevens ferdigheter innen lesing, skriving, ordforråd, muntlig kommunikasjon og uttale
        </p>

        {/* Total fremdrift */}
        {selectedStudent && Object.keys(moduleProgress).length > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '32px',
            maxWidth: '400px',
            margin: '0 auto 32px auto'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
              Fremdrift for {selectedStudent}
            </h3>
            <div style={{
              width: '100%',
              height: '12px',
              backgroundColor: '#e5e7eb',
              borderRadius: '6px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{
                width: `${totalProgress()}%`,
                height: '100%',
                backgroundColor: '#3b82f6',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6', textAlign: 'center' }}>
              {totalProgress()}%
            </div>
          </div>
        )}
      </div>

      {/* Datavarsel */}
      {showDataWarning && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h4 style={{
                margin: '0 0 8px 0',
                color: '#92400e',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                ⚠️ Viktig informasjon om datalagring
              </h4>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#92400e',
                lineHeight: '1.5'
              }}>
                Kartleggingsdata lagres kun så lenge denne nettleserfanen er åpen. 
                For lengre pauser eller hvis du må lukke fanen, bruk "Eksporter fremdrift" 
                før du avslutter. Du kan senere bruke "Importer fremdrift" for å fortsette der du slapp.
              </p>
            </div>
            <button
              onClick={dismissWarning}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#92400e',
                padding: '0',
                marginLeft: '16px'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Elev-administrasjon */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '40px'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 24px 0',
          textAlign: 'center'
        }}>
          Elev-administrasjon
        </h3>

        {/* Legg til ny elev */}
        <div style={{
          maxWidth: '500px',
          margin: '0 auto 32px auto',
          padding: '24px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px'
        }}>
          <h4 style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#374151',
            margin: '0 0 12px 0'
          }}>
            Legg til ny elev
          </h4>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                value={currentStudentName}
                onChange={(e) => setCurrentStudentName(e.target.value)}
                placeholder="Skriv elevens navn..."
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none'
                }}
                onKeyPress={(e) => e.key === 'Enter' && addNewStudent()}
              />
            </div>
            <button
              onClick={addNewStudent}
              disabled={!currentStudentName.trim()}
              style={{
                padding: '12px 20px',
                backgroundColor: currentStudentName.trim() ? '#3b82f6' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: currentStudentName.trim() ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}
            >
              ➕ Legg til
            </button>
          </div>
        </div>

        {/* Elev-liste */}
        {studentList.length > 0 ? (
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#374151',
              margin: '0 0 16px 0',
              textAlign: 'center'
            }}>
              Velg elev å kartlegge ({studentList.length} elev{studentList.length !== 1 ? 'er' : ''} registrert)
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              {studentList.map(student => {
                const isSelected = selectedStudent === student.name;
                const studentProgress = isSelected && Object.keys(moduleProgress).length > 0 ? totalProgress() : 0;
                
                return (
                  <div
                    key={student.name}
                    style={{
                      padding: '16px',
                      backgroundColor: isSelected ? '#dbeafe' : '#f8fafc',
                      border: isSelected ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => selectStudent(student.name)}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <h5 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: isSelected ? '#1e40af' : '#111827',
                          margin: '0 0 4px 0'
                        }}>
                          {isSelected ? '👤 ' : ''}{student.name}
                        </h5>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          Opprettet: {new Date(student.created).toLocaleDateString('no-NO')}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            exportStudentProgress(student.name);
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                          title="Eksporter data for denne eleven"
                        >
                          💾
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteStudent(student.name);
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                          title="Slett data for denne eleven"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    {isSelected && studentProgress > 0 && (
                      <div style={{
                        backgroundColor: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '8px'
                      }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          Gjennomsnittlig fremdrift: {studentProgress}%
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${studentProgress}%`,
                            height: '100%',
                            backgroundColor: '#10b981',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '16px',
            padding: '32px'
          }}>
            Ingen elever registrert ennå. Legg til en elev for å starte kartlegging.
          </div>
        )}
      </div>

      {/* Kartleggingsområder */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '40px'
      }}>
        {modules.map(module => (
          <ModuleCard key={module.id} module={module} />
        ))}
      </div>

      {/* Verktøy */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 24px 0'
        }}>
          Verktøy og rapporter
        </h3>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <button
            onClick={() => {
              // Lagre aktiv elev data før navigering
              if (selectedStudent) {
                const studentData = {
                  name: selectedStudent,
                  startTime: new Date().toISOString()
                };
                sessionStorage.setItem('kartlegging_student', JSON.stringify(studentData));
              }
              window.location.href = './rapport/index.html';
            }}
            disabled={!selectedStudent || Object.keys(moduleProgress).length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: selectedStudent && Object.keys(moduleProgress).length > 0 ? '#3b82f6' : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedStudent && Object.keys(moduleProgress).length > 0 ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            📊 Se rapport for {selectedStudent || 'valgt elev'}
          </button>

          <button
            onClick={() => selectedStudent ? exportStudentProgress(selectedStudent) : exportAllProgress()}
            disabled={studentList.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: studentList.length > 0 ? '#059669' : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: studentList.length > 0 ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            💾 Eksporter {selectedStudent ? `data for ${selectedStudent}` : 'alle elever'}
          </button>

          <div style={{ position: 'relative' }}>
            <input
              type="file"
              accept=".json"
              onChange={importProgress}
              style={{ display: 'none' }}
              id="import-file"
            />
            <button
              onClick={() => document.getElementById('import-file').click()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              📂 Importer elevdata
            </button>
          </div>

          <button
            onClick={clearAllData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            🗑️ Slett all data
          </button>
        </div>
      </div>
    </div>
  );
};

export default KartleggingLauncher;